import { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { notification } from 'tea-component';
import { getInDevTitleSpendTrend } from '@sg/services';
import { chartOrderedColors } from '@sg/utils/useChartOptions';
import {
  createBtnPoint,
  InDevProjectsTab,
  SGBuriedBtnPoint,
} from '@/pages/strategic/utils/buriedPoint';
import { DefaultFinancialsChat } from '../../../../../pageConfig';
import type { TInDevChartViewKey } from '../../../../../hooks/useInDevChartEyesState';

const SPEND_METRIC_KEYS = [
  'rd_staff_cost',
  'outsourcing_cost',
  'other_rd_cost',
  'ga_overheads',
  'vgtr',
] as const;

const SPEND_SERIES_META: Array<{
  key: (typeof SPEND_METRIC_KEYS)[number];
  name: string;
}> = [
  {
    key: 'rd_staff_cost',
    name: 'R&D Staff Cost',
  },
  {
    key: 'outsourcing_cost',
    name: 'Outsourcing',
  },
  {
    key: 'other_rd_cost',
    name: 'Other R&D',
  },
  {
    key: 'ga_overheads',
    name: 'G&A Overheads',
  },
  {
    key: 'vgtr',
    name: 'VGTR',
  },
];

type TSpendMetricMap = Record<(typeof SPEND_METRIC_KEYS)[number], number | null>;

const EMPTY_SPEND_METRICS: TSpendMetricMap = {
  rd_staff_cost: null,
  outsourcing_cost: null,
  other_rd_cost: null,
  ga_overheads: null,
  vgtr: null,
};

const computeTotalFromMetrics = (metrics: TSpendMetricMap): number | null => {
  const values = SPEND_METRIC_KEYS
    .map((metricKey) => metrics[metricKey])
    .filter((value) => value !== null && value !== undefined);
  if (!values.length) {
    return null;
  }
  return values.reduce((sum, value) => sum + Number(value), 0);
};

const toNumberOrNull = (value: any): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const resolveIsActual = (value: any): boolean => {
  if (value === 1 || value === '1' || value === true) {
    return true;
  }
  return false;
};

interface IUseSpendTrendDataParams {
  projectId: string;
  selectedMonth?: string | null;
  activeDateTypeSegment: string;
  projectInfo: string;
  viewEnabled: boolean;
  updateInDevEyesViewState: (viewKey: TInDevChartViewKey, enabled: boolean) => void;
}

export const useSpendTrendData = ({
  projectId,
  selectedMonth,
  activeDateTypeSegment,
  projectInfo,
  viewEnabled,
  updateInDevEyesViewState,
}: IUseSpendTrendDataParams) => {
  const [tableData, setTableData] = useState<any[]>([]);
  const [updateTime, setUpdateTime] = useState<string>('');
  const [showChatsType, setShowChatsType] = useState<any>(DefaultFinancialsChat);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    setShowChatsType((prev: any) => ({
      ...prev,
      format: viewEnabled,
    }));
  }, [viewEnabled]);

  const handleChatsTypeChange = useCallback(
    (e: any, downloadValue?: string) => {
      const currentViewEnabled = !!viewEnabled;
      if (downloadValue === 'download') {
        const btnPoint = createBtnPoint(
          InDevProjectsTab,
          'spendTrendDownload',
          `In Dev Title Spend Trend - Download - ${projectInfo}`,
        );
        if (btnPoint) {
          SGBuriedBtnPoint(btnPoint);
        }
      } else if (e?.value && e.value !== 'download') {
        const value = e.value;
        const isViewClick = value === 'view'
          || (
            e.format !== showChatsType?.format
            && value === showChatsType?.value
          );

        if (isViewClick) {
          const nextViewEnabled = !currentViewEnabled;
          const btnPoint = createBtnPoint(
            InDevProjectsTab,
            'spendTrendView',
            `In Dev Title Spend Trend - View - ${nextViewEnabled ? 'On' : 'Off'} - ${projectInfo}`,
          );
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
          updateInDevEyesViewState('titleSpend', nextViewEnabled);
          setShowChatsType((prev: any) => ({
            ...prev,
            format: nextViewEnabled,
          }));
          return;
        }

        if (value === 'bar') {
          if (showChatsType?.value !== 'bar') {
            const btnPoint = createBtnPoint(
              InDevProjectsTab,
              'spendTrendBarChart',
              `In Dev Title Spend Trend - Bar Chart - ${projectInfo}`,
            );
            if (btnPoint) {
              SGBuriedBtnPoint(btnPoint);
            }
          }
        } else if (value === 'table') {
          if (showChatsType?.value !== 'table') {
            const btnPoint = createBtnPoint(
              InDevProjectsTab,
              'spendTrendTable',
              `In Dev Title Spend Trend - Table - ${projectInfo}`,
            );
            if (btnPoint) {
              SGBuriedBtnPoint(btnPoint);
            }
          }
        }
      }

      setShowChatsType({
        ...e,
        format: currentViewEnabled,
      });
    },
    [projectInfo, showChatsType, viewEnabled, updateInDevEyesViewState],
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let res: any;
      let nextTableData: any[] = [];

      if (activeDateTypeSegment === 'phase') {
        const payload: {
          project_id: string;
          view_mode: 'by_phase';
          datatime?: string;
        } = {
          project_id: projectId,
          view_mode: 'by_phase',
        };
        if (selectedMonth) {
          payload.datatime = selectedMonth;
        }
        res = await getInDevTitleSpendTrend(payload);
        const responseData = res?.data || {};
        const phaseRows = Array.isArray(responseData?.data) ? responseData.data : [];
        nextTableData = phaseRows.map((item: any) => ({
          date: item?.phase || item?.month || item?.date || '-',
          total: computeTotalFromMetrics({
            ...EMPTY_SPEND_METRICS,
            rd_staff_cost: toNumberOrNull(item?.metrics?.rd_staff_cost),
            outsourcing_cost: toNumberOrNull(item?.metrics?.outsourcing_cost),
            other_rd_cost: toNumberOrNull(item?.metrics?.other_rd_cost),
            ga_overheads: toNumberOrNull(item?.metrics?.ga_overheads),
            vgtr: toNumberOrNull(item?.metrics?.vgtr),
          }),
          rd_staff_cost: toNumberOrNull(item?.metrics?.rd_staff_cost),
          outsourcing_cost: toNumberOrNull(item?.metrics?.outsourcing_cost),
          other_rd_cost: toNumberOrNull(item?.metrics?.other_rd_cost),
          ga_overheads: toNumberOrNull(item?.metrics?.ga_overheads),
          vgtr: toNumberOrNull(item?.metrics?.vgtr),
          isActual: resolveIsActual(item?.is_actual) ? 1 : 0,
          isCurrent: resolveIsActual(item?.is_current) ? 1 : 0,
        }));
        setUpdateTime(
          responseData?.datatime
            ? moment(responseData?.datatime).format('YYYY-MM')
            : '',
        );
      } else {
        const payload: {
          project_id: string;
          view_mode: 'by_month';
          datatime?: string;
        } = {
          project_id: projectId,
          view_mode: 'by_month',
        };
        if (selectedMonth) {
          payload.datatime = selectedMonth;
        }
        res = await getInDevTitleSpendTrend(payload);
        const responseData = res?.data || {};
        const monthRows = Array.isArray(responseData?.data) ? responseData.data : [];
        nextTableData = monthRows.map((item: any) => {
          const metrics = {
            ...EMPTY_SPEND_METRICS,
            rd_staff_cost: toNumberOrNull(item?.metrics?.rd_staff_cost),
            outsourcing_cost: toNumberOrNull(item?.metrics?.outsourcing_cost),
            other_rd_cost: toNumberOrNull(item?.metrics?.other_rd_cost),
            ga_overheads: toNumberOrNull(item?.metrics?.ga_overheads),
            vgtr: toNumberOrNull(item?.metrics?.vgtr),
          };
          return {
            date: item?.month || moment(item?.date).format('YYYY-MM'),
            total: computeTotalFromMetrics(metrics),
            ...metrics,
            isActual: resolveIsActual(item?.is_actual) ? 1 : 0,
            isCurrent: resolveIsActual(item?.is_current) ? 1 : 0,
          };
        });
        setUpdateTime(
          responseData?.datatime
            ? moment(responseData?.datatime).format('YYYY-MM')
            : '',
        );
      }

      setTableData(nextTableData);
    } catch (error) {
      console.error('Error fetching financials data:', error);
      notification.error({
        description: 'Failed to fetch financials data',
      });
      setTableData([]);
      setUpdateTime('');
    } finally {
      setLoading(false);
    }
  }, [activeDateTypeSegment, selectedMonth, projectId]);

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    fetchData();
  }, [fetchData, projectId]);

  const chartData = useMemo(
    () => ({
      xAxisData: tableData.map((item: any) => item.date),
      seriesData: [
        ...SPEND_SERIES_META.map((metricMeta, index) => ({
          name: metricMeta.name,
          type: 'bar',
          stack: 'title-spend',
          barWidth: '40%',
          // Keep ordered palette for most metrics, but pin VGTR to product-specified pink.
          color:
            metricMeta.key === 'vgtr'
              ? '#F17C86'
              : chartOrderedColors[index] || chartOrderedColors[0],
          data: tableData.map((item: any) => {
            const value = item?.[metricMeta.key];
            const isActual = item?.isActual === 1;
            return {
              value,
              tooltipWidth: activeDateTypeSegment === 'phase' ? '280px' : '250px',
              tooltipHeaderRight: isActual ? 'Actual' : 'Forecast',
              // Let shared tooltip formatter recalculate total from visible series
              // so legend hide/show always updates Total Dev Costs correctly.
              totalFromVisible: true,
              totalTitleText: 'Total Dev Costs',
              itemStyle: {
                opacity: isActual ? 1 : 0.3,
              },
            };
          }),
        })),
      ],
    }),
    [tableData, activeDateTypeSegment],
  );

  const isPhaseScrollable =
    activeDateTypeSegment === 'phase' && tableData.length > 5;

  return {
    tableData,
    chartData,
    updateTime,
    loading,
    showChatsType,
    handleChatsTypeChange,
    isPhaseScrollable,
  };
};
